// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { VpnSiteLinks } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type {
  VpnSiteLink,
  VpnSiteLinksListByVpnSiteNextOptionalParams,
  VpnSiteLinksListByVpnSiteOptionalParams,
  VpnSiteLinksListByVpnSiteResponse,
  VpnSiteLinksGetOptionalParams,
  VpnSiteLinksGetResponse,
  VpnSiteLinksListByVpnSiteNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing VpnSiteLinks operations. */
export class VpnSiteLinksImpl implements VpnSiteLinks {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class VpnSiteLinks class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Lists all the vpnSiteLinks in a resource group for a vpn site.
   * @param resourceGroupName The resource group name of the VpnSite.
   * @param vpnSiteName The name of the VpnSite.
   * @param options The options parameters.
   */
  public listByVpnSite(
    resourceGroupName: string,
    vpnSiteName: string,
    options?: VpnSiteLinksListByVpnSiteOptionalParams,
  ): PagedAsyncIterableIterator<VpnSiteLink> {
    const iter = this.listByVpnSitePagingAll(resourceGroupName, vpnSiteName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listByVpnSitePagingPage(resourceGroupName, vpnSiteName, options, settings);
      },
    };
  }

  private async *listByVpnSitePagingPage(
    resourceGroupName: string,
    vpnSiteName: string,
    options?: VpnSiteLinksListByVpnSiteOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<VpnSiteLink[]> {
    let result: VpnSiteLinksListByVpnSiteResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listByVpnSite(resourceGroupName, vpnSiteName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listByVpnSiteNext(
        resourceGroupName,
        vpnSiteName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listByVpnSitePagingAll(
    resourceGroupName: string,
    vpnSiteName: string,
    options?: VpnSiteLinksListByVpnSiteOptionalParams,
  ): AsyncIterableIterator<VpnSiteLink> {
    for await (const page of this.listByVpnSitePagingPage(
      resourceGroupName,
      vpnSiteName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Retrieves the details of a VPN site link.
   * @param resourceGroupName The resource group name of the VpnSite.
   * @param vpnSiteName The name of the VpnSite.
   * @param vpnSiteLinkName The name of the VpnSiteLink being retrieved.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    vpnSiteName: string,
    vpnSiteLinkName: string,
    options?: VpnSiteLinksGetOptionalParams,
  ): Promise<VpnSiteLinksGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, vpnSiteName, vpnSiteLinkName, options },
      getOperationSpec,
    );
  }

  /**
   * Lists all the vpnSiteLinks in a resource group for a vpn site.
   * @param resourceGroupName The resource group name of the VpnSite.
   * @param vpnSiteName The name of the VpnSite.
   * @param options The options parameters.
   */
  private _listByVpnSite(
    resourceGroupName: string,
    vpnSiteName: string,
    options?: VpnSiteLinksListByVpnSiteOptionalParams,
  ): Promise<VpnSiteLinksListByVpnSiteResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, vpnSiteName, options },
      listByVpnSiteOperationSpec,
    );
  }

  /**
   * ListByVpnSiteNext
   * @param resourceGroupName The resource group name of the VpnSite.
   * @param vpnSiteName The name of the VpnSite.
   * @param nextLink The nextLink from the previous successful call to the ListByVpnSite method.
   * @param options The options parameters.
   */
  private _listByVpnSiteNext(
    resourceGroupName: string,
    vpnSiteName: string,
    nextLink: string,
    options?: VpnSiteLinksListByVpnSiteNextOptionalParams,
  ): Promise<VpnSiteLinksListByVpnSiteNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, vpnSiteName, nextLink, options },
      listByVpnSiteNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/vpnSites/{vpnSiteName}/vpnSiteLinks/{vpnSiteLinkName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VpnSiteLink,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.vpnSiteName,
    Parameters.vpnSiteLinkName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listByVpnSiteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/vpnSites/{vpnSiteName}/vpnSiteLinks",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ListVpnSiteLinksResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.vpnSiteName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listByVpnSiteNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ListVpnSiteLinksResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.vpnSiteName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
